import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import userSettingsService from '../services/userSettings.service';

interface NotificationPreferencesContextType {
  showDriverRequestToast: boolean;
  setShowDriverRequestToast: (enabled: boolean) => void;
  showOnlyMyDrivers: boolean;
  setShowOnlyMyDrivers: (enabled: boolean) => void;
  /**
   * The persistent maintenance "repair order request" toasts. OPT-OUT: absent
   * storage means ON, like showDriverRequestToast. The toasts are the point of
   * the feature (the Requests column ships hidden), so defaulting them off
   * would hide the feature from the people it was built for.
   *
   * Toasts ONLY. The nav count badge is passive and is deliberately not gated
   * on this — silencing the pop-ups must not also hide the queue.
   */
  showRepairOrderToast: boolean;
  setShowRepairOrderToast: (enabled: boolean) => void;
  /**
   * The persistent market-conditions toasts raised when someone saves a note.
   * OPT-OUT: absent storage means ON, like the two above -- every user is
   * meant to see a market go sideways.
   *
   * Toasts ONLY. The header pill reads the shared store, which keeps
   * refreshing on the same broadcast; silencing the pop-up must not also
   * stop the pill updating.
   */
  showMarketConditionsToast: boolean;
  setShowMarketConditionsToast: (enabled: boolean) => void;
  isLoading: boolean;
}

const NotificationPreferencesContext = createContext<NotificationPreferencesContextType | undefined>(undefined);

const STORAGE_KEY = 'notification_preferences';
const LS_DRIVER_REQUEST_TOAST = 'showDriverRequestToast';
const LS_ONLY_MY_DRIVERS = 'showOnlyMyDrivers';
const LS_REPAIR_ORDER_TOAST = 'showRepairOrderToast';
const LS_MARKET_CONDITIONS_TOAST = 'showMarketConditionsToast';

interface Prefs {
  showDriverRequestToast: boolean;
  showOnlyMyDrivers: boolean;
  showRepairOrderToast: boolean;
  showMarketConditionsToast: boolean;
}

// EVERY localStorage touch is wrapped. This provider wraps the whole app, so a
// private window (where getItem throws) or a full quota must degrade to the
// in-memory defaults, never take down every page in the product.
const readFlag = (key: string, fallback: boolean): boolean => {
  try {
    const saved = window.localStorage.getItem(key);
    return saved === null ? fallback : saved === 'true';
  } catch {
    return fallback;
  }
};

const writeFlag = (key: string, value: boolean): void => {
  try {
    window.localStorage.setItem(key, value.toString());
  } catch {
    // Storage unavailable. The preference still applies for this session and
    // is still saved server-side; it just will not survive a refresh offline.
  }
};

export const NotificationPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Seeded synchronously from localStorage so there is no flash of the default.
  const [prefs, setPrefs] = useState<Prefs>(() => ({
    showDriverRequestToast: readFlag(LS_DRIVER_REQUEST_TOAST, true),
    showOnlyMyDrivers: readFlag(LS_ONLY_MY_DRIVERS, false),
    showRepairOrderToast: readFlag(LS_REPAIR_ORDER_TOAST, true),
    showMarketConditionsToast: readFlag(LS_MARKET_CONDITIONS_TOAST, true),
  }));
  const [isLoading, setIsLoading] = useState(true);

  /**
   * The authoritative latest value, updated SYNCHRONOUSLY by the setters.
   * Reading the render-scoped state to build the save payload sends a stale
   * value for every other key when two toggles are flipped before React has
   * re-rendered — the payload is a whole-object overwrite, so that silently
   * reverts the other preference.
   */
  const prefsRef = useRef(prefs);

  const applyPrefs = (next: Prefs) => {
    prefsRef.current = next;
    setPrefs(next);
  };

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const setting = await userSettingsService.getUserSetting(STORAGE_KEY);
        if (setting && setting.setting_value) {
          const saved = typeof setting.setting_value === 'string'
            ? JSON.parse(setting.setting_value)
            : setting.setting_value;

          const next = { ...prefsRef.current };
          const adopt = (key: keyof Prefs, lsKey: string) => {
            if (saved[key] !== undefined) {
              next[key] = !!saved[key];
              writeFlag(lsKey, next[key]);
            }
          };
          adopt('showDriverRequestToast', LS_DRIVER_REQUEST_TOAST);
          adopt('showOnlyMyDrivers', LS_ONLY_MY_DRIVERS);
          adopt('showRepairOrderToast', LS_REPAIR_ORDER_TOAST);
          adopt('showMarketConditionsToast', LS_MARKET_CONDITIONS_TOAST);
          applyPrefs(next);
        }
      } catch {
        // Silently fall back to localStorage values
      } finally {
        setIsLoading(false);
      }
    };
    loadPreferences();
  }, []);

  const savePreferences = async (next: Prefs) => {
    try {
      await userSettingsService.saveUserSetting(STORAGE_KEY, next);
    } catch {
      // localStorage is the fallback
    }
  };

  /** One path for every toggle: newest-wins payload, mirror, then server. */
  const setPref = async (key: keyof Prefs, lsKey: string, enabled: boolean) => {
    const next = { ...prefsRef.current, [key]: enabled };
    applyPrefs(next);
    writeFlag(lsKey, enabled);
    await savePreferences(next);
  };

  const setShowDriverRequestToast = (enabled: boolean) =>
    setPref('showDriverRequestToast', LS_DRIVER_REQUEST_TOAST, enabled);

  const setShowOnlyMyDrivers = (enabled: boolean) =>
    setPref('showOnlyMyDrivers', LS_ONLY_MY_DRIVERS, enabled);

  const setShowRepairOrderToast = (enabled: boolean) =>
    setPref('showRepairOrderToast', LS_REPAIR_ORDER_TOAST, enabled);

  const setShowMarketConditionsToast = (enabled: boolean) =>
    setPref('showMarketConditionsToast', LS_MARKET_CONDITIONS_TOAST, enabled);

  return (
    <NotificationPreferencesContext.Provider
      value={{
        showDriverRequestToast: prefs.showDriverRequestToast,
        setShowDriverRequestToast,
        showOnlyMyDrivers: prefs.showOnlyMyDrivers,
        setShowOnlyMyDrivers,
        showRepairOrderToast: prefs.showRepairOrderToast,
        setShowRepairOrderToast,
        showMarketConditionsToast: prefs.showMarketConditionsToast,
        setShowMarketConditionsToast,
        isLoading,
      }}
    >
      {children}
    </NotificationPreferencesContext.Provider>
  );
};

export const useNotificationPreferences = (): NotificationPreferencesContextType => {
  const context = useContext(NotificationPreferencesContext);
  if (!context) {
    throw new Error('useNotificationPreferences must be used within a NotificationPreferencesProvider');
  }
  return context;
};
